import { Avatar, Skeleton, Tabs, Tooltip } from "antd";
import {
  UserRound,
  FileText,
  Download,
  CheckCircle,
  Clock,
  Upload,
  CheckCircle2 
} from "lucide-react";
import { Suspense, useMemo, lazy } from "react";
import { useSelector } from "react-redux";
import { getModelColor, getModelDisplayName } from "../../constants/modelMeta";
import "./UserHistoryDashboard.css";
import { displayName, formatName, formatTimestamp, parseNumber } from "../../Utils/helperMethod";
import { usePerformanceSummary } from "../../hooks/usePerformanceSummary";
import { useNavigate } from "react-router-dom";

const BenfordChart = lazy(() =>
  import("../charts/ModalCharts/BenfordChart")
);
const PdfChart = lazy(() =>
  import("../charts/ModalCharts/PdfChart")
);
const RsfChart = lazy(() =>
  import("../charts/ModalCharts/RsfChart")
);
const MonteCarloChart = lazy(() =>
  import("../charts/ModalCharts/MonteCarloChart")
);
const ZScoreScatterChart = lazy(() =>
  import("../charts/ModalCharts/ZscoreChart")
);
const LocalOutlierScatterChart = lazy(() =>
  import("../charts/ModalCharts/LocalOutlierChart")
);
const EnsembleDonutConsensus = lazy(() =>
  import("../DataHistory/Ensemble/EnsembleDonutConsensus")
);
const RandomSamplingTable = lazy(() =>
  import("../DataHistory/RandomSampling/RandomSamplingTable")
);


const OUTLIER_FIELD_MAP = {
  benford: ["Outlier_Benford", "outlier_benford"],
  pdf: ["Outlier_Pdf", "Outlier_PDF", "outlier_pdf"],
  lof: ["Outlier_LOF", "Outlier_Lof", "outlier_lof"],
  rsf: ["Outlier_RSF", "Outlier_Rsf", "outlier_rsf"],
  zscore: ["Outlier_ZScore", "outlier_zscore"],
  montecarlo: ["Outlier_Montecarlo", "outlier_montecarlo"],
};

const ALL_MODELS = [
  "benford",
  "pdf",
  "lof",
  "rsf",
  "zscore",
  "montecarlo",
];

/* --------------------------- File Tab --------------------------- */

const FileTabContent = ({ filename, result }) => {
  const processedChartData = useMemo(() => {
    if (!Array.isArray(result?.data) || !result.data.length) {
      return { scatterData: [], depVar: null, groupBy: [] };
    }

    const rows = result.data;
    const groupBy = Array.isArray(result.groupby_columns)
      ? result.groupby_columns
      : [];

    const availableCols = Object.keys(rows[0] || {});
    const numericCols = availableCols.filter(
      (c) => !Number.isNaN(parseNumber(rows[0][c]))
    );

    const depVar =
      result.dependent_variable ||
      result.dep_variable ||
      numericCols[0] ||
      null;

    if (!depVar || !availableCols.includes(depVar)) {
      return { scatterData: [], depVar, groupBy };
    }

    /* ---------- group categories ---------- */
    const categories = [];
    const groupIndex = {};

    if (groupBy.length) {
      [...new Set(
        rows.map((r) =>
          groupBy.map((c) => r[c] ?? "N/A").join(" | ")
        )
      )]
        .sort()
        .forEach((g, i) => {
          categories.push(g);
          groupIndex[g] = i;
        });
    }

    const model =
      Array.isArray(result.models_selected) &&
      result.models_selected.length === 1
        ? result.models_selected[0].toLowerCase()
        : null;

    const outlierFields = OUTLIER_FIELD_MAP[model] || [];

    const scatterData = rows
      .map((row, i) => {
        const y = parseNumber(row[depVar]);
        if (Number.isNaN(y)) return null;

        const outlierVal =
          outlierFields
            .map((f) => row[f])
            .find((v) => v !== undefined && v !== null) ?? 0;

        const exception = Number(outlierVal) === 1 ? 1 : 0;

        if (groupBy.length) {
          const label = groupBy.map((c) => row[c] ?? "N/A").join(" | ");
          return {
            x:groupIndex[label] + (i % 10) * 0.01,
            y,
            exception,
            zScore: row.Z_SCORE || row.z_score || 0,
            group: label,
            row,
            categories,
          };
        }

        return {
          x: i + 1,
          y,
          exception,
          zScore: row.Z_SCORE || row.z_score || 0,
          group: `Row ${i + 1}`,
          row,
          categories: [],
        };
      })
      .filter(Boolean);

    return { scatterData, depVar, groupBy, rows };
  }, [result]);

  const renderChart = () => {
    const { scatterData, depVar, groupBy, rows } = processedChartData;

    if (!scatterData.length) {
      return (
        <div className="text-center p-8 text-gray-400">
          No data available for this chart
        </div>
      );
    }

    const models = (result.models_selected || []).map((m) =>
      m.toLowerCase()
    );

    const hasAllModels =
      models.length === ALL_MODELS.length &&
      ALL_MODELS.every((m) => models.includes(m));

    /* ---------- Ensemble ---------- */
    if (hasAllModels) {
      const summary = result.outlier_summary || {};
      const order = [...ALL_MODELS, "ensemble"];

      const data = order.map((k) => ({
        key: k,
        name: getModelDisplayName(k) || k,
        value: Number(summary[k]) || 0,
        color: getModelColor(k),
      }));

      return (
          <EnsembleDonutConsensus data={data} />
      );
    }

    const model = models[0];

    const commonProps = {
      data: scatterData,
      dep: depVar,
      groupBy,
      rows,
    };

    const chartMap = {
      sampling: <RandomSamplingTable samplingPayload={commonProps?.rows} />,
      benford: (
        <BenfordChart
          data={scatterData.map((p) => ({
            Actual: p.row?.Actual ?? p.row?.actual ?? null,
            Actualvalue:
              p.row?.Actualvalue ??
              p.row?.ActualValue ??
              p.row?.actualvalue ??
              null,
            Benford: p.row?.Benford ?? null,
          }))}
          groupLabel={
            groupBy.length
              ? groupBy.map(displayName).join(" | ")
              : "Group"
          }
          valueLabel={displayName(depVar) || "Value"}
        />
      ),
      pdf: <PdfChart {...commonProps} />,
      rsf: <RsfChart {...commonProps} />,
      lof: <LocalOutlierScatterChart {...commonProps} />,
      montecarlo: (
        <MonteCarloChart
          {...commonProps}
          categories={scatterData[0]?.categories || []}
        />
      ),
    };

    return (
        chartMap[model] || (
          <ZScoreScatterChart
            data={scatterData}
            groupBy={groupBy}
            useCategories={!!groupBy.length}
            categories={scatterData[0]?.categories || []}
            xLabel={
              groupBy.length
                ? groupBy.map(displayName).join(", ")
                : "Row Index"
            }
            yLabel={displayName(depVar) || "Value"}
          />
        )
    );
  };

  return (
    <div className="grid gap-6 rounded-2xl bg-[#0b0b0f] p-4 md:p-6 border border-gray-600">
      <div className="w-full">
        {/* Header row */}
        <div className="flex flex-col gap-3 mb-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3 min-w-0 w-full sm:w-auto">
            <FileText className="text-green-300 shrink-0" size={28} />
            <h3 className="text-white font-bold text-base sm:text-lg md:text-xl line-clamp-2 sm:line-clamp-1 break-all">
              {filename}
            </h3>
          </div>

          {/* Status badge */}
          <span className="self-start sm:self-auto md:ml-auto w-fit bg-green-400/20 text-green-300 px-3 py-2 rounded-full text-xs sm:text-sm flex items-center gap-1">
            <CheckCircle size={16} />
            {result.status}
          </span>
        </div>

        {/* Timestamp */}
        <div className="flex items-center gap-2 mb-4 text-[11px] sm:text-xs text-gray-400">
          <Clock size={14} />
          {formatTimestamp(result.timestamp)}
        </div>

        {/* Models */}
        <div className="flex flex-wrap gap-2 sm:gap-3 mb-4 sm:mb-6">
          {result.models_selected.map((m) => (
            <span
              key={m}
              className=" px-3 sm:px-5 py-1.5 sm:py-2 text-xs sm:text-sm rounded-full bg-gradient-to-r from-blue-500 to-pink-500 text-white"
            >
              {getModelDisplayName(m) || m}
            </span>
          ))}
        </div>
      </div>

      <div className="w-full sm:w-auto  md:w-12/12  h-[360px]  sm:h-[400px]  md:h-[600px]  px-1  sm:p-5   overflow-hidden">
        <Suspense fallback={<Skeleton active paragraph={{ rows: 8 }} />}>
          {renderChart()}
        </Suspense>
      </div>
      <a
        href={result.download_link}
        target="_blank"
        rel="noopener noreferrer"
        className="w-full sm:w-1/2 md:w-4/12 bg-gradient-to-r from-blue-500 to-pink-500 !text-white py-3 px-4 rounded-xl flex items-center justify-center gap-2  overflow-hidden  sm:hover:scale-105"
      >
        <Download size={16} />
        Download CSV
      </a>
    </div>
  );
};

const TabsSkeleton = () => (
  <div className="space-y-6">
    {/* Tab headers */}
    <div className="flex gap-3">
      {[1, 2, 3].map((i) => (
        <Skeleton.Button
          key={i}
          active
          size="small"
          style={{ width: 120, height: 36 }}
        />
      ))}
    </div>

    {/* Chart area */}
    <div
      className="w-full rounded-2xl bg-[#111118] p-4 sm:p-6 h-[300px] sm:h-[400px] md:h-[600px]"
    >
      <Skeleton active paragraph={{ rows: 10 }} />
    </div>
  </div>
);

const AnalysisEmptyState = ({ onUploadClick }) => {
  return (
    <div className="flex-1 flex items-center justify-center px-8 py-8">
      <div className="max-w-xl w-full text-center">

        {/* Icon */}
        <div className="mb-8 flex justify-center">
          <div className="relative">
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-purple-600/10 to-blue-600/10 flex items-center justify-center border border-purple-600/20">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-600/20 to-blue-600/20 flex items-center justify-center">
                <Clock
                  className="w-8 h-8 text-purple-400"
                  strokeWidth={1.5}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Heading */}
        <h2 className="text-4xl font-bold mb-4 bg-gradient-to-r from-purple-400 via-purple-300 to-blue-400 bg-clip-text text-transparent">
          No Data History Yet
        </h2>

        <p className="text-gray-300 text-base mb-12 leading-tight">
          Your analysis journey begins with a single upload. Start tracking your
          datasets, reviewing past analyses, and building your data intelligence history.
        </p>

        {/* Feature List */}
        <div className="mb-12 space-y-4 text-left max-w-md mx-auto">
          {[
            "Track and manage your last five uploaded datasets",
            "Access previous anomaly detection results",
            "View analysis timestamps and timeline",
            "Download historical data",
          ].map((text, index) => (
            <div key={index} className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-purple-400 mt-0.5 flex-shrink-0" />
              <p className="text-gray-300 text-base">{text}</p>
            </div>
          ))}
        </div>

        {/* CTA Button */}
        <div className="flex justify-center">
          <button
            onClick={onUploadClick}
            className="px-8 py-4 bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl font-semibold hover:from-purple-500 hover:to-blue-500 transition-all shadow-lg shadow-purple-600/30 flex items-center justify-center gap-2"
          >
            <Upload className="w-5 h-5" />
            Upload Your First Dataset
          </button>
        </div>

      </div>
    </div>
  );
};


/* ------------------------ Dashboard ------------------------ */

const UserHistoryDashboard = () => {
  const navigate = useNavigate();
  const { data, loading } = usePerformanceSummary();
  const user = useSelector((s) => s.auth.user);

  const formattedUser = formatName(user);

  const handleGetStarted = () => {
    navigate("/dashboard/data-entry");
  };

  const truncateFilename = (name, length = 15) =>
    name && name.length > length ? name.slice(0, length) + "…" : name;

  const processedFiles = useMemo(() => {
    return Array.isArray(data?.processed_files) ? data.processed_files : null;
  }, [data?.processed_files]);

  const hasFiles = useMemo(() => {
    return processedFiles !== null && processedFiles.length > 0;
  }, [processedFiles]);

  const isDataReady = useMemo(() => {
    return !loading && processedFiles !== null;
  }, [loading, processedFiles]);

  const tabItems = useMemo(() => {
    if (!hasFiles) return [];

    return processedFiles.map((filename) => ({
      key: filename,
      label: (
        <Tooltip title={filename}>
          <span className="flex items-center gap-2 max-w-[160px] text-gray-200">
            <FileText size={14} className="text-purple-300" />
            <span className="truncate text-sm">
              {truncateFilename(filename, 15)}
            </span>
          </span>
        </Tooltip>
      ),
      children: (
        <FileTabContent filename={filename} result={data?.result?.[filename]} />
      ),
    }));
  }, [hasFiles, processedFiles, data?.result]);

  return (
    <div className="min-h-screen bg-[#0b0b0f] text-white p-4 md:p-8">
      {/* Header */}
      <div className="flex items-center justify-center gap-4">
        <Avatar
          size="large"
          className="bg-gradient-to-br from-purple-500 to-blue-500"
          icon={<UserRound />}
        />
        <h1 className="text-2xl md:text-3xl font-semibold bg-gradient-to-r from-blue-500 to-pink-500 bg-clip-text text-transparent">
          {hasFiles ? (
            <>Welcome Back, {formattedUser}!</>
          ) : (
            <>Welcome, {formattedUser}!</>
          )}
        </h1>
      </div>

      {(hasFiles) && (
        <div className="flex justify-center mb-4">
          <p className="text-gray-300 text-lg">
            Access Summary of Your Latest Analysis
          </p>
        </div>
      )}

      {/* Main Content */}
      {!isDataReady ? (
        <TabsSkeleton />
      ) : !hasFiles ? (
        <AnalysisEmptyState onUploadClick={handleGetStarted} />
      ) : (
        <Tabs
          items={tabItems}
          type="card"
          className="custom-tabs"
          tabBarGutter={20}
          destroyOnHidden
        />
      )}

      {/* Footer */}
      {hasFiles && (
        <div className="flex justify-center gap-6 mt-6">
          <button
            onClick={handleGetStarted}
            className="px-10 py-4 rounded-xl bg-gray-200 text-black font-semibold text-base transition-transform hover:scale-105 flex items-center justify-center gap-2"
          >
            <Upload className="w-5 h-5" />
            New Upload
          </button>
        </div>
      )}
    </div>
  );
};

export default UserHistoryDashboard;
